/* eslint-disable @typescript-eslint/no-explicit-any */
import { generateRandomUID, generateUID } from '~/utils/dicomUid';
import { DICOMUtils } from './dicom_utils';
import { ModalityAggregationRule } from './models/anonymization_rule';

export class AnonymizationStorage {
    private _anonymousUIDs: { [key: string]: { [key: string]: string } };
    private _originalUIDs: { [key: string]: { [key: string]: string } };

    constructor() {
        this._anonymousUIDs = {};
        this._originalUIDs = {};
    }

    public initializeDatasetAnonymization(
        taskUID: string,
        dataset: any
    ): [string, string] {
        const originalPatientID = dataset.PatientID;
        const originalPatientName = dataset.PatientName[0]?.Alphabetic;

        if (this._anonymousUIDs[taskUID]) {
            return [taskUID, this._anonymousUIDs[taskUID]['datasetKey']];
        }

        let patientID = generateRandomUID(8);

        while (Object.keys(this._originalUIDs).includes(patientID)) {
            patientID = generateRandomUID(8);
        }

        const patientName =
            originalPatientID !== originalPatientName
                ? `anon-patient-${patientID}`
                : patientID; // If the patient name is the same as the ID, we can't generate a different name!

        this._anonymousUIDs[taskUID] = {};
        this._anonymousUIDs[taskUID]['datasetKey'] = patientID;
        this._anonymousUIDs[taskUID][originalPatientID] = patientID;
        if (originalPatientID !== originalPatientName) {
            // Don't overwrite the patient ID if it's the same as the name
            this._anonymousUIDs[taskUID][originalPatientName] = patientName;
        }
        this._originalUIDs[patientID] = {};
        this._originalUIDs[patientID][patientID] = originalPatientID;
        if (patientID !== patientName) {
            // Don't overwrite the patient ID if it's the same as the name
            this._originalUIDs[patientID][patientName] = originalPatientName;
        }
        return [taskUID, patientID];
    }

    public copyAnonymization(
        originalKey: string,
        datasetKey: string,
        dataset: any,
        aggregationRule: { [key: string]: ModalityAggregationRule },
        modelType: string
    ): void {
        let rule: ModalityAggregationRule;
        if (aggregationRule[modelType.toLowerCase()]) {
            rule = aggregationRule[modelType.toLowerCase()];
        } else {
            rule = aggregationRule['general'];
        }

        const patientID = dataset.PatientID;
        const aggAttribute = DICOMUtils.attributeByRule(dataset, rule);
        const newDatasetKey = `${patientID}-${aggAttribute}`;

        if (newDatasetKey !== datasetKey) {
            this._anonymousUIDs[originalKey] = {
                ...this._anonymousUIDs[originalKey],
            };
            this._originalUIDs[newDatasetKey] = {
                ...this._originalUIDs[datasetKey],
            };
        }
    }

    public anonymizeString(
        value: string,
        originalKey: string,
        datasetKey: string
    ): string {
        if (this._anonymousUIDs[originalKey]) {
            if (this._anonymousUIDs[originalKey][value]) {
                return this._anonymousUIDs[originalKey][value];
            } else {
                const uid = generateRandomUID(8);
                this._anonymousUIDs[originalKey][value] = uid;
                this._originalUIDs[datasetKey][uid] = value;
            }
        } else {
            this._anonymousUIDs[originalKey] = {};
            const uid = generateRandomUID(8);
            this._anonymousUIDs[originalKey][value] = uid;
            this._originalUIDs[originalKey][uid] = value;
        }

        return this._anonymousUIDs[originalKey][value];
    }

    public anonymizeUID(
        value: string,
        originalKey: string,
        datasetKey: string
    ): string {
        if (this._anonymousUIDs[originalKey]) {
            if (this._anonymousUIDs[originalKey][value]) {
                return this._anonymousUIDs[originalKey][value];
            } else {
                const uid = generateUID();
                this._anonymousUIDs[originalKey][value] = uid;
                this._originalUIDs[datasetKey][uid] = value;
            }
        } else {
            this._anonymousUIDs[originalKey] = {};
            const uid = generateUID();
            this._anonymousUIDs[originalKey][value] = uid;
            this._originalUIDs[originalKey][uid] = value;
        }

        return this._anonymousUIDs[originalKey][value];
    }

    public saveOriginalMapping(
        key: string,
        originalValue: string,
        datasetKey: string
    ): void {
        this._originalUIDs[datasetKey][key] = originalValue;
    }

    public hasDataset(datasetKey: string): boolean {
        return datasetKey in this._originalUIDs;
    }

    public hasOriginalValue(key: string, datasetKey: string): boolean {
        return (
            datasetKey in this._originalUIDs &&
            key in this._originalUIDs[datasetKey]
        );
    }

    public getOriginalValue(key: string, datasetKey: string): string {
        return this._originalUIDs[datasetKey][key];
    }

    public saveAnonymizedMapping(
        key: string,
        anonValue: string,
        datasetKey: string
    ): void {
        this._anonymousUIDs[datasetKey][key] = anonValue;
    }
}
